import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import PopUp from 'shared/components/pop-up/pop-up';

import { ResidentValidateTransmitterResultType } from 'features/residents/models/resident-validate-transmitter-result-type';
import ValidateTransmitterResponse from 'features/residents/models/validate-transmitter-response';
import LinkableTransmitterStepsProviderContext from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/providers/contexts/linkable-transmitter-steps-provider-context';
import styles from './validate-transmitter-popup.module.scss';

const ValidateTransmitterPopup = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings', {
    keyPrefix: 'linkableDevicesModal.linkableTransmitterSteps.validateTransmitterPopup',
  });
  const {
    validationPopupState: { validateTransmitterResponse, closeValidateTransmitterPopup },
  } = use(LinkableTransmitterStepsProviderContext);

  const getTitle = (): string => {
    switch (validateTransmitterResponse?.validationResultType) {
      case ResidentValidateTransmitterResultType.AlreadyExistsNotLinked:
        return t('alreadyExistsNotLinkedTitle');
      case ResidentValidateTransmitterResultType.AlreadyExistsLinkedToResidentSameLocation:
        return t('alreadyExistsLinkedToResidentSameLocationTitle');
      case ResidentValidateTransmitterResultType.AlreadyExistsLinkedToResidentOtherLocation:
        return t('alreadyExistsLinkedToResidentOtherLocationTitle');
      default:
        return t('unknownTitle');
    }
  };

  const renderMessage = (): React.JSX.Element => {
    let message = '';

    switch (validateTransmitterResponse?.validationResultType) {
      case ResidentValidateTransmitterResultType.AlreadyExistsNotLinked:
        message = t('alreadyExistsNotLinked');
        break;
      case ResidentValidateTransmitterResultType.AlreadyExistsLinkedToResidentSameLocation:
        message = t('alreadyExistsLinkedToResidentSameLocation');
        break;
      case ResidentValidateTransmitterResultType.AlreadyExistsLinkedToResidentOtherLocation:
        message = t('alreadyExistsLinkedToResidentOtherLocation');
        break;
      default:
        message = t('unknown');
        break;
    }

    return (
      <Typography className={styles.text} variant="subtitle1">
        {message}
      </Typography>
    );
  };

  const renderBreadCrumbs = (validateTransmitterResponse: ValidateTransmitterResponse): React.JSX.Element => {
    if (
      validateTransmitterResponse.validationResultType !==
        ResidentValidateTransmitterResultType.AlreadyExistsLinkedToResidentSameLocation ||
      !validateTransmitterResponse.breadCrumbs ||
      validateTransmitterResponse.breadCrumbs.length === 0
    ) {
      return <></>;
    }

    return (
      <BreadCrumb
        showEllipsis={validateTransmitterResponse.breadCrumbs.length > 1}
        allElementNames={validateTransmitterResponse.breadCrumbs}
      >
        {validateTransmitterResponse.breadCrumbs.map((breadCrumb, index) => (
          <Typography
            className={styles.organisationUnitName}
            key={`ValidateTransmitter_Breadcrumb_${breadCrumb}`}
            data-testid={`validate-transmitter-breadcrumb-${index}`}
            variant="caption"
          >
            {breadCrumb}
          </Typography>
        ))}
      </BreadCrumb>
    );
  };

  const renderOrganisationUnitName = (validateTransmitterResponse: ValidateTransmitterResponse): React.JSX.Element => {
    if (
      validateTransmitterResponse.validationResultType !==
        ResidentValidateTransmitterResultType.AlreadyExistsLinkedToResidentOtherLocation ||
      !validateTransmitterResponse.breadCrumbs ||
      validateTransmitterResponse.breadCrumbs.length === 0
    ) {
      return <></>;
    }

    return (
      <Typography className={styles.organisationUnitName} variant="subtitle1">
        {validateTransmitterResponse.breadCrumbs[0]}
      </Typography>
    );
  };

  const renderSecondMessage = (): React.JSX.Element => {
    if (
      validateTransmitterResponse?.validationResultType !== ResidentValidateTransmitterResultType.AlreadyExistsLinkedToResidentSameLocation
    ) {
      return <></>;
    }

    return (
      <Typography className={styles.text} variant="subtitle1">
        {t('alreadyExistsLinkedToResidentSameLocationSecondMessage')}
      </Typography>
    );
  };

  const renderBody = (): React.JSX.Element => {
    if (!validateTransmitterResponse) {
      return <></>;
    }

    return (
      <div className={styles.validateTransmitterPopup}>
        {renderMessage()}
        {renderBreadCrumbs(validateTransmitterResponse)}
        {renderOrganisationUnitName(validateTransmitterResponse)}
        {renderSecondMessage()}
      </div>
    );
  };

  return (
    <PopUp
      isOpen={!!validateTransmitterResponse}
      title={getTitle()}
      body={renderBody()}
      primaryButtonText={t('closeButtonText')}
      handleOnClose={closeValidateTransmitterPopup}
      primaryButtonAction={closeValidateTransmitterPopup}
    />
  );
};

export default ValidateTransmitterPopup;
